import { UserPlus, Users as UsersIcon } from "lucide-react";
import { useState } from "react";
import { Button, Empty, ErrorNote, Modal, Spinner, toast } from "../components/ui";
import { api, APP_NAMES, APP_ROLES, useApi, type AppId, type Me } from "../lib/api";

interface UserRow {
  email: string;
  name?: string;
  status: "invited" | "active" | "disabled";
  tenantAdmin: boolean;
  entitlements: Partial<Record<AppId, { role: string; connections?: string[] }>>;
}

/** Tenant admin: invite users, set app roles, admin flag, disable (design §4.3). */
export default function UsersPage({ me }: { me: Me }) {
  const { data, error, loading, reload } = useApi<{ users: UserRow[] }>("/admin/users");
  const [editing, setEditing] = useState<UserRow | "new" | null>(null);
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink">Users</h1>
          <p className="text-sm text-muted">{me.tenant!.signInMode === "federated" ? "People you add here can sign in with your organization's single sign-on." : "People you add get an email invitation from the portal."}</p>
        </div>
        <Button variant="primary" onClick={() => setEditing("new")}>
          <UserPlus className="size-4" aria-hidden /> Add user
        </Button>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : data?.users.length === 0 ? (
        <Empty icon={<UsersIcon className="size-8" />} title="No users yet" />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line bg-surface">
          <table className="w-full text-sm">
            <thead className="text-left text-muted border-b border-line">
              <tr>
                <th className="px-4 py-2 font-medium">User</th>
                <th className="px-4 py-2 font-medium">Apps</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {data?.users.map((u) => (
                <tr key={u.email} className="border-b border-line last:border-0">
                  <td className="px-4 py-3">
                    <div className="font-medium text-ink">
                      {u.name || u.email} {u.tenantAdmin && <span className="ml-1 text-xs rounded bg-accent-chip text-accent-text px-1.5 py-0.5">Admin</span>}
                    </div>
                    {u.name && <div className="text-xs text-muted">{u.email}</div>}
                  </td>
                  <td className="px-4 py-3 text-ink-2">
                    {(Object.entries(u.entitlements) as [AppId, { role: string }][]).map(([a, e]) => `${APP_NAMES[a]} (${e.role})`).join(", ") || "None"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={u.status === "active" ? "text-good" : u.status === "invited" ? "text-warn" : "text-bad"}>{u.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" onClick={() => setEditing(u)}>
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <UserForm me={me} user={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} onSaved={() => (setEditing(null), void reload())} />}
    </div>
  );
}

function UserForm({ me, user, onClose, onSaved }: { me: Me; user?: UserRow; onClose: () => void; onSaved: () => void }) {
  const licensed = me.tenant!.apps;
  const [email, setEmail] = useState(user?.email ?? "");
  const [name, setName] = useState(user?.name ?? "");
  const [tenantAdmin, setTenantAdmin] = useState(user?.tenantAdmin ?? false);
  const [roles, setRoles] = useState<Partial<Record<AppId, string>>>(Object.fromEntries(Object.entries(user?.entitlements ?? {}).map(([a, e]) => [a, e!.role])));
  const [disabled, setDisabled] = useState(user?.status === "disabled");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const self = user?.email === me.email;

  const save = async () => {
    setBusy(true);
    setError(null);
    // Keep any connection limits already on an entitlement; connection-scoped roles arrive with Infor connections.
    const entitlements = Object.fromEntries(Object.entries(roles).filter(([, r]) => r).map(([a, role]) => [a, { ...user?.entitlements[a as AppId], role }]));
    try {
      if (user) await api(`/admin/users/${encodeURIComponent(user.email)}`, { method: "PUT", body: { name, tenantAdmin, entitlements, ...(self ? {} : { status: disabled ? "disabled" : "active" }) } });
      else await api("/admin/users", { body: { email, name, tenantAdmin, entitlements } });
      toast(user ? "User saved" : "User added");
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm";
  return (
    <Modal
      title={user ? `Edit ${user.name || user.email}` : "Add user"}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" busy={busy} onClick={() => void save()}>
            {user ? "Save" : "Add user"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        {!user && (
          <label className="block text-sm">
            <span className="text-ink-2 font-medium">Email</span>
            <input className={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
        )}
        <label className="block text-sm">
          <span className="text-ink-2 font-medium">Name</span>
          <input className={input} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <fieldset className="text-sm space-y-2">
          <legend className="text-ink-2 font-medium mb-1">Apps</legend>
          {licensed.length === 0 && <p className="text-muted">No apps are licensed for {me.tenant!.name} yet.</p>}
          {licensed.map((a) => (
            <label key={a} className="flex items-center justify-between gap-3">
              <span>{APP_NAMES[a]}</span>
              <select className="h-8 rounded-lg border border-line bg-surface px-2" value={roles[a] ?? ""} onChange={(e) => setRoles((r) => ({ ...r, [a]: e.target.value }))}>
                <option value="">No access</option>
                {APP_ROLES[a].map((r) => (
                  <option key={r} value={r}>
                    {r[0].toUpperCase() + r.slice(1)}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </fieldset>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={tenantAdmin} disabled={self} onChange={(e) => setTenantAdmin(e.target.checked)} />
          Tenant admin (manages users and set-up)
        </label>
        {user && !self && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={disabled} onChange={(e) => setDisabled(e.target.checked)} />
            Disabled (can't sign in)
          </label>
        )}
      </div>
    </Modal>
  );
}
